import EventEmitter from './event-emitter.js';

export default class PhysicsEngine {
	#attraction;
	#frictionFactor;
	#velocityBoost;
	#velocity;
	#currentValue;
	#targetValue;
	#startValue;
	#isAnimating;
	#prevTime;
	#eventEmitter;
	#animationId;

	/**
	 * creates an instance of physicsengine.
	 * @param {number} [attraction=0.026] - the attraction value for physics-based animation (0 < attraction < 1).
	 * @param {number} [friction=0.28] - the friction value for physics-based animation (0 < friction < 1).
	 * @param {number} [velocityBoost=1.4] - multiplier applied to initial velocity for snappier response.
	 */
	constructor({ attraction = 0.026, friction = 0.28, velocityBoost = 1.4 } = {}) {
		const _ = this;
		_.#validateAttraction(attraction);
		_.#validateFriction(friction);

		_.#attraction = attraction;
		_.#frictionFactor = 1 - friction;
		_.#velocityBoost = velocityBoost;

		_.#velocity = 0;
		_.#currentValue = 0;
		_.#targetValue = 0;
		_.#startValue = 0;

		_.#isAnimating = false;
		_.#prevTime = null;
		_.#animationId = 0; // start at zero

		_.#eventEmitter = new EventEmitter();
	}

	/**
	 * animates from a start value to an end value.
	 * @param {number} startValue - the starting value.
	 * @param {number} endValue - the target value.
	 * @param {number} initialVelocity - the initial velocity.
	 */
	animateTo(startValue, endValue, initialVelocity) {
		const _ = this;

		// if something is already animating, stop it first
		if (_.#isAnimating) {
			_.stop();
		}

		if (isNaN(endValue)) {
			console.warn(`PhysicsEngine.animateTo: endValue is NaN (received: ${endValue})`);
			return;
		}

		// increment #animationId to mark this as our "active" animation
		const localAnimationId = ++_.#animationId;

		// apply a velocity boost for snappier response
		initialVelocity *= _.#velocityBoost;

		_.#startValue = startValue;
		_.#currentValue = startValue;
		_.#targetValue = endValue;
		_.#velocity = initialVelocity;
		_.#isAnimating = true;
		_.#prevTime = null;

		// emit initial position to trigger first frame
		_.#eventEmitter.emit('engine:position-changed', {
			position: _.#currentValue,
			positionDelta: 0,
			progress: 0,
			velocity: _.#velocity,
		});
	}

	/**
	 * Advances the physics simulation by one frame.
	 * Called externally by the frame engine to sync with main render loop.
	 * @param {number} time - the timestamp from the frame engine
	 */
	tick(time) {
		const _ = this;

		if (!_.#isAnimating) return;

		// Calculate time delta (default to ~120fps for first frame to minimize initial movement)
		// Clamp to prevent huge jumps after tab switches or pauses
		let timeDelta = _.#prevTime == null ? 8.33 : time - _.#prevTime;
		_.#prevTime = time;
		timeDelta = Math.max(0, Math.min(timeDelta, 100));

		// Time factor normalized to 60fps (16.67ms reference)
		const timeFactor = timeDelta / 16.67;

		// Spring physics: force proportional to displacement
		const displacement = _.#targetValue - _.#currentValue;
		const force = displacement * _.#attraction;

		// Apply force and friction (frame-rate independent)
		_.#velocity += force * timeFactor;
		_.#velocity *= Math.pow(_.#frictionFactor, timeFactor);

		// Update position
		const posDelta = _.#velocity * timeFactor;
		_.#currentValue += posDelta;

		// Calculate progress
		const totalDistance = _.#targetValue - _.#startValue;
		const distanceCovered = _.#currentValue - _.#startValue;
		const progress = totalDistance !== 0 ? distanceCovered / totalDistance : 0;

		// Check completion
		if (Math.abs(posDelta) < 0.01 && Math.abs(displacement) < 0.1) {
			_.#isAnimating = false;
			_.#currentValue = _.#targetValue;
			_.#eventEmitter.emit('engine:position-changed', {
				position: _.#currentValue,
				positionDelta: 0,
				progress: 1,
				velocity: 0,
			});
			_.#eventEmitter.emit('engine:finished');
			return;
		}

		_.#eventEmitter.emit('engine:position-changed', {
			position: _.#currentValue,
			positionDelta: posDelta,
			progress,
			velocity: _.#velocity,
		});
	}

	/**
	 * stops the ongoing animation immediately.
	 */
	stop() {
		this.#isAnimating = false;
	}

	/**
	 * returns whether we are currently animating.
	 * @returns {boolean}
	 */
	isAnimating() {
		return this.#isAnimating;
	}

	/**
	 * sets the attraction value
	 * @param {number} attraction - must be a number between 0 and 1 (exclusive).
	 */
	setAttraction(attraction) {
		this.#validateAttraction(attraction);
		this.#attraction = attraction;
	}

	/**
	 * sets the friction value
	 * @param {number} friction - must be a number between 0 and 1 (exclusive).
	 */
	setFriction(friction) {
		this.#validateFriction(friction);
		this.#frictionFactor = 1 - friction;
	}

	/**
	 * sets the velocity boost multiplier.
	 * @param {number} velocityBoost - multiplier for initial velocity.
	 */
	setVelocityBoost(velocityBoost) {
		this.#velocityBoost = velocityBoost;
	}

	/**
	 * adds an event listener for the specified event.
	 * @param {string} eventName - the name of the event.
	 * @param {function} eventFunction - the function to call when the event is triggered.
	 */
	on(eventName, eventFunction) {
		this.#eventEmitter.on(eventName, eventFunction);
	}

	/**
	 * remove an event listener for the specified event.
	 * @param {string} eventName - the name of the event.
	 * @param {function} eventFunction - the function to remove
	 */
	off(eventName, eventFunction) {
		this.#eventEmitter.off(eventName, eventFunction);
	}

	#validateAttraction(attraction) {
		if (typeof attraction !== 'number' || attraction <= 0 || attraction >= 1) {
			throw new Error('Attraction must be a number between 0 and 1 (exclusive).');
		}
	}

	#validateFriction(friction) {
		if (typeof friction !== 'number' || friction <= 0 || friction >= 1) {
			throw new Error('Friction must be a number between 0 and 1 (exclusive).');
		}
	}

	destroy() {
		// stop any ongoing animation
		this.stop();

		this.#eventEmitter.destroy();
		// clear event emitter reference
		this.#eventEmitter = null;
	}
}
